// ReviewQueue (the price book round, DEC-019; concept C's "work through them"): one record at a time
// from a filtered list, to approve, change or skip, then the next. Its head says which list and where
// you are in it ("No customer words: 1 of 6"), with a bar and what the list holds; any action on the
// whole list sits beside it (Approve all 3 of Jev's drafts). The record is the caller's. Its foot:
// the keys, Skip, and the one main action (Approve and next); a viewer who can't approve has none,
// and the record says what they can do instead. Keys, when the focus isn't in a field: A approve,
// E edit, S skip, J next, K back; Escape leaves, and the focus goes back to what opened it. At the
// end it says so. Reads only --queue-*; Button and Link read their own.
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';
import { Link } from '../Link/Link.tsx';
import './ReviewQueue.css';

export interface ReviewQueueProps {
  /** The list it works through ("No customer words"). */
  label: string;
  /** Where you are: 0-based, of total. */
  index: number;
  total: number;
  /** What the list holds ("3 with Jev's draft, 3 with none yet"). */
  summary?: string;
  /** Actions on the whole list, beside the bar: Link elements. */
  extra?: ReactNode;
  /** The record. */
  children: ReactNode;
  /** The main action ("Approve and next"); without onPrimary there's none (who can't approve). */
  primaryLabel?: string;
  onPrimary?: () => void;
  onEdit?: () => void;
  onSkip: () => void;
  onNext: () => void;
  onBack: () => void;
  /** Escape, or Back to the list. */
  onClose: () => void;
  closeLabel?: string;
  /** At the end: what was done ("All 6 have customer words."). */
  doneText?: string;
}

const inField = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

export function ReviewQueue({
  label,
  index,
  total,
  summary,
  extra,
  children,
  primaryLabel = 'Approve and next',
  onPrimary,
  onEdit,
  onSkip,
  onNext,
  onBack,
  onClose,
  closeLabel = 'Back to the list',
  doneText = 'That’s all of them.',
}: ReviewQueueProps) {
  const box = useRef<HTMLElement>(null);
  // in when it opens; back to what opened it when it leaves (critic K18)
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // without scrolling: the page stays where it was, its head above the queue (the sticky frame stays put)
    box.current?.focus({ preventScroll: true });
    return () => opener?.focus?.();
  }, []);
  const done = index >= total;
  const keys: [string, string, (() => void) | undefined][] = [
    ['a', 'approve', onPrimary],
    ['e', 'edit', onEdit],
    ['s', 'skip', onSkip],
    ['j', 'next', onNext],
    ['k', 'back', onBack],
  ];
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      return onClose();
    }
    if (inField(e.target) || e.metaKey || e.ctrlKey || e.altKey || done) return;
    const k = keys.find(([key]) => key === e.key.toLowerCase());
    if (!k?.[2]) return;
    e.preventDefault();
    k[2]();
  };
  const at = Math.min(index + 1, total);
  return (
    <section
      ref={box}
      className="fl-queue"
      aria-label={`${label}: ${at} of ${total}`}
      tabIndex={-1}
      onKeyDown={onKey}
    >
      <div className="fl-queue__head">
        <p className="fl-queue__where">
          <b className="fl-queue__strong">
            {label}: {at} of {total}
          </b>
          {summary ? <span className="fl-queue__muted">{summary}</span> : null}
        </p>
        <span
          className="fl-queue__bar"
          role="progressbar"
          aria-label="Where you are"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={Math.min(index + 1, total)}
        >
          <span
            className="fl-queue__fill"
            style={{ inlineSize: `${(Math.min(index + 1, total) / Math.max(total, 1)) * 100}%` }}
          />
        </span>
        <span className="fl-queue__extra">
          {extra}
          <Link onPress={onClose}>{closeLabel}</Link>
        </span>
      </div>
      <div className="fl-queue__body">{done ? <p className="fl-queue__say">{doneText}</p> : children}</div>
      {done ? null : (
        <div className="fl-queue__foot">
          <p className="fl-queue__keys">
            {keys
              .filter(([, , f]) => f)
              .map(([k, what]) => `${k.toUpperCase()} ${what}`)
              .join(' · ')}
          </p>
          <span className="fl-queue__acts">
            <Link onPress={onSkip}>Skip</Link>
            {onPrimary ? (
              <Button variant="primary" onPress={onPrimary}>
                {primaryLabel}
              </Button>
            ) : (
              <Button onPress={onNext}>Next</Button>
            )}
          </span>
        </div>
      )}
    </section>
  );
}
